<script setup>
  import { computed } from 'vue';
  import TextInput from "@/components/forms/inputs/text-input.vue";
  import MarkdownRenderer from "@/components/MarkdownRenderer.vue";
  
  const props = defineProps({
    criterion: Object,
    index: Number,
    allCriteria: {
      type: Array,
      default: () => []
    },
    isEditing: {
      type: Boolean,
      default: false
    },
    isReadOnly: {
      type: Boolean,
      default: false
    },
    isAgentic: {
      type: Boolean,
      default: false
    }
  });
  
  const emit = defineEmits(['update', 'delete', 'moveUp', 'moveDown']);

  const AGENTIC_GRADER_OPTIONS = [
    { label: 'LLM-as-a-Judge', value: 'llm-judge' },
    { label: 'Exact Match', value: 'ExactMatch' }
  ];

  const resolveCriterionGraderType = (criterion = {}) => {
    if (criterion.graderType === 'ExactMatch' || criterion.grader_type === 'ExactMatch') {
      return 'ExactMatch';
    }
    if (criterion.graderType === 'llm-judge' || criterion.grader_type === 'llm-judge') {
      return 'llm-judge';
    }
    return 'llm-judge';
  };

  const selectedGraderType = computed(() => resolveCriterionGraderType(props.criterion));
  const isExactMatchCriterion = computed(() => selectedGraderType.value === 'ExactMatch');
  const gradingFieldLabel = computed(() => (
    isExactMatchCriterion.value ? 'Expected Match Value' : 'Explicit Grading Criterion'
  ));
  const gradingFieldPlaceholder = computed(() => (
    isExactMatchCriterion.value
      ? 'Exact text that must match, e.g. "42".'
      : 'Instructions for the judge, e.g. "The final answer is 21.". Markdown and LaTeX supported. Click preview to render.'
  ));
    
  function preventInvalidKeystrokes(e) {
    const allowedKeys = ['Backspace', 'Delete', 'ArrowLeft', 'ArrowRight', 'Tab', 'Enter'];
    if (allowedKeys.includes(e.key)) return;
    if (e.key === 'e' || e.key === 'E') { e.preventDefault();}
  }
  
  function handleWeightUpdate(value) {
    if (value === '' || value === null) {
      emit('update', { ...props.criterion, weight: 0 }); 
      return;
    }
  
    let normalized = String(value).replace(',', '.');
    let numValue = parseFloat(normalized);
  
    if (isNaN(numValue)) return;
  
    if (numValue > 100) {
      numValue = 100;
    }
  
    if (numValue <= 0) {
       numValue = 0.01;
    }
  
    const strVal = String(numValue);
    if (strVal.includes('.')) {
      const parts = strVal.split('.');
      if (parts[1].length > 2) {
        numValue = parseFloat(numValue.toFixed(2));
      }
    }
  
    const updated = { ...props.criterion, weight: numValue };
    emit('update', updated);
  }
  
  function updateField(field, value) {
    const updated = { ...props.criterion, [field]: value };
    if (props.isAgentic) {
      updated.graderType = resolveCriterionGraderType(props.criterion);
    }
    emit('update', updated);
  }

  function updateGraderType(value) {
    const normalized = value === 'ExactMatch' ? 'ExactMatch' : 'llm-judge';
    emit('update', {
      ...props.criterion,
      graderType: normalized
    });
  }
  </script>
  
  <template>
    <div class="bg-echoes-background-black border border-echoes-border-gray rounded-xl p-4">
      <div class="flex items-end gap-4 mb-3">
        <div class="flex-1">
          <label :for="`criterion-name-${index}`" class="text-sm text-white font-semibold mb-1.5 flex items-center gap-2">
            Criterion Name <span class="text-red-500">*</span>
            <v-tooltip location="top" max-width="300">
              <template v-slot:activator="{ props: tooltipProps }">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                  class="w-4 h-4 cursor-help flex-shrink-0 text-echoes-primary pointer-events-auto" v-bind="tooltipProps">
                  <path fill="currentColor"
                    d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1,1.25,0,0,0,12,7.5Z">
                  </path>
                </svg>
              </template>
              <span>Short label for this scoring dimension. Examples: "Correct final answer", "Uses given constraints"</span>
            </v-tooltip>
          </label>
          <div :class="{ 'pointer-events-none': props.isEditing || props.isReadOnly }">
            <TextInput :id="`criterion-name-${index}`" v-model="criterion.name"
              @update:modelValue="updateField('name', $event)" placeholder="Enter criterion name"
              :disabled="props.isReadOnly"
              border-class="border-echoes-border-gray" hover-border-class="border-echoes-primary/40"
              focus-border-class="border-echoes-primary"
              :dataTestId="`criterion-name-input-${index}`" />
          </div>
        </div>

        <div v-if="props.isAgentic" class="w-44">
          <label :for="`criterion-grader-${index}`" class="text-sm text-white font-semibold mb-1.5 flex items-center gap-2">
            Grader
          </label>
          <div :class="{ 'pointer-events-none': props.isEditing || props.isReadOnly }">
            <select
              :id="`criterion-grader-${index}`"
              :value="selectedGraderType"
              @change="updateGraderType($event.target.value)"
              :disabled="props.isReadOnly"
              class="w-full h-[42px] rounded-lg border border-echoes-border-gray bg-transparent px-3 text-sm text-white focus:outline-none focus:border-echoes-primary disabled:cursor-not-allowed"
              :data-testid="`criterion-grader-type-${index}`"
            >
              <option
                v-for="option in AGENTIC_GRADER_OPTIONS"
                :key="option.value"
                :value="option.value"
                class="bg-echoes-background-black text-white"
              >
                {{ option.label }}
              </option>
            </select>
          </div>
        </div>
        
        <div class="w-32">
          <label :for="`criterion-weight-${index}`"
            class="text-sm text-white font-semibold mb-1.5 flex items-center gap-2">
            Weight
          </label>
          <div class="flex items-center gap-2" :class="{ 'pointer-events-none': props.isEditing || props.isReadOnly }">
            <TextInput 
              :id="`criterion-weight-${index}`" 
              type="number" 
              :model-value="criterion.weight"
              @update:modelValue="handleWeightUpdate" 
              @keydown="preventInvalidKeystrokes" 
              placeholder="0.00" 
              :min="0.01"
              :max="100" 
              step="0.01"
              :disabled="props.isReadOnly"
              bg-class="bg-transparent" 
              border-class="border-echoes-border-gray"
              hover-border-class="border-echoes-primary/40" 
              focus-border-class="border-echoes-primary"
              :dataTestId="`criterion-weight-input-${index}`"
            />
            <span class="text-sm text-white/60 font-medium">%</span>
          </div>
        </div>
        
        <button
          :disabled="props.isReadOnly"
          :class="[
            'bg-transparent border border-red-500/40 text-red-400 text-sm font-medium px-4 py-2.5 rounded-lg transition-all flex items-center justify-center gap-2',
            props.isReadOnly || props.isEditing ? 'cursor-not-allowed pointer-events-none' : 'cursor-pointer hover:bg-red-500/10'
          ]"
          @click="emit('delete')" title="Delete criterion"
          :data-testid="`delete-criterion-button-${index}`">
          <i class="uil uil-trash-alt"></i>
        </button>
      </div>
  
      <div class="flex flex-col gap-4">
        <div class="flex flex-col gap-2">
          <label :for="`criterion-description-${index}`" class="text-sm text-white font-semibold gap-2 flex items-center">
            What are you checking for? <span class="text-white/40">(optional)</span>
          </label>
          <div :class="{ 'pointer-events-none': props.isEditing || props.isReadOnly }">
            <TextInput :id="`criterion-description-${index}`" v-model="criterion.description"
              @update:modelValue="updateField('description', $event)"
              placeholder="Brief description of what this criterion evaluates"
              :disabled="props.isReadOnly"
              bg-class="bg-transparent"
              border-class="border-echoes-border-gray" hover-border-class="border-echoes-primary/40"
              focus-border-class="border-echoes-primary"
              :dataTestId="`criterion-description-input-${index}`" />
          </div>
        </div>

        <div class="flex flex-col gap-2">
          <label :for="`semantic-prompt-${index}`" class="text-sm text-white font-semibold flex items-center gap-2">
            {{ gradingFieldLabel }}<span class="text-red-500">*</span>
            <v-tooltip location="top" max-width="400">
              <template v-slot:activator="{ props: tooltipProps }">
                <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24"
                  class="w-4 h-4 cursor-help flex-shrink-0 text-echoes-primary pointer-events-auto" v-bind="tooltipProps">
                  <path fill="currentColor"
                    d="M12,2A10,10,0,1,0,22,12,10.01114,10.01114,0,0,0,12,2Zm0,18a8,8,0,1,1,8-8A8.00917,8.00917,0,0,1,12,20Zm0-8.5a1,1,0,0,0-1,1v3a1,1,0,0,0,2,0v-3A1,1,0,0,0,12,11.5Zm0-4a1.25,1.25,0,1,0,1.25,1.25A1,1.25,0,0,0,12,7.5Z">
                  </path>
                </svg>
              </template>
              <span>List the specific, verifiable, and objective requirements used to judge a response against this criterion. Include precise numbers and values from the answer if applicable. For example, if the final answer is "5", make sure to specify that in the grading criterion for the judge.</span>
            </v-tooltip>
          </label>
          <div class="criterion-semantic-section" :class="{ 'pointer-events-none': props.isEditing }">
            <MarkdownRenderer 
              :model-value="criterion.semanticPrompt"
              @update:model-value="updateField('semanticPrompt', $event)"
              :placeholder="gradingFieldPlaceholder"
              :disabled="props.isReadOnly"
              :content-only="false"
              :enable-image-upload="false"
              class="flex-1 min-h-0 w-full h-full"
              :dataTestId="`criterion-semantic-prompt-input-${index}`" />
          </div>
        </div>
      </div>
    </div>
  </template>

<style scoped>
.criterion-semantic-section {
  min-height: 300px;
  display: flex;
  flex-direction: column;
}

.criterion-semantic-section :deep(.markdown-container) {
  min-height: 300px;
}

.criterion-semantic-section :deep(.markdown-container:not(.preview-mode)) {
  height: 300px;
}

.criterion-semantic-section :deep(.markdown-container.preview-mode) {
  height: auto;
}
</style>
